// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/enterprise-admin/users/UserDetailDrawer.jsx

import React from "react";
import UserStatusBadge from "./UserStatusBadge";

function formatDate(value) {
  if (!value) return "Not recorded";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Unknown" : date.toLocaleString();
}

function humanize(value) {
  return String(value || "unknown")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function timelineLabel(event) {
  return humanize(
    event.action ||
      `${event.previous_status || "unknown"} to ${
        event.new_status || "unknown"
      }`
  );
}

function roleDescription(roles, roleKey) {
  return (
    roles.find((role) => role.role_key === roleKey)?.description ||
    "Enterprise role authority."
  );
}

export default function UserDetailDrawer({
  user,
  roles = [],
  loading,
  saving,
  currentUserId,
  onAssignRole,
  onRemoveRole,
  onLifecycleAction,
}) {
  if (!user) {
    return (
      <aside className="enterprise-admin-user-detail">
        <div className="enterprise-admin-empty compact">
          <strong>Select a member</strong>
          <p>
            Choose a user to review identity, authority, access state, and
            audit history.
          </p>
        </div>
      </aside>
    );
  }

  const assignedRoles = Array.isArray(user.assigned_roles)
    ? user.assigned_roles
    : [];
  const isCurrentUser = user.user_id === currentUserId;
  const active = user.status === "active";
  const suspended = user.status === "suspended";
  const removed = user.status === "removed";
  const legacyRole = removed ? null : user.legacy_role || null;
  const effectiveAssignedRoles = removed ? [] : assignedRoles;
  const effectiveRoles = Array.from(
    new Set([legacyRole, ...effectiveAssignedRoles].filter(Boolean))
  );

  return (
    <aside className="enterprise-admin-user-detail">
      <div className="enterprise-admin-detail-heading">
        <p className="enterprise-admin-eyebrow">Member Detail</p>
        <h3>{user.display_name || user.email || "Unnamed member"}</h3>
        <UserStatusBadge status={user.status} />
        {isCurrentUser ? (
          <span className="enterprise-admin-current-user-label">
            Current session
          </span>
        ) : null}
      </div>

      {loading ? (
        <div className="enterprise-admin-detail-loading">
          Loading verified user detail…
        </div>
      ) : null}

      <dl className="enterprise-admin-definition-list">
        <div>
          <dt>Email</dt>
          <dd>{user.email || "Not provided by identity token"}</dd>
        </div>
        <div>
          <dt>Identity provider</dt>
          <dd>{humanize(user.identity_provider)}</dd>
        </div>
        <div>
          <dt>MFA</dt>
          <dd>{humanize(user.mfa_status)}</dd>
        </div>
        <div>
          <dt>Last login</dt>
          <dd>{formatDate(user.last_login_at)}</dd>
        </div>
        <div>
          <dt>Member since</dt>
          <dd>{formatDate(user.created_at)}</dd>
        </div>
        <div>
          <dt>User ID</dt>
          <dd className="enterprise-admin-mono-wrap">{user.user_id}</dd>
        </div>
      </dl>

      <section className="enterprise-admin-detail-section">
        <div className="enterprise-admin-card-heading compact">
          <h4>Effective authority</h4>
          <span>
            {effectiveRoles.length} effective role
            {effectiveRoles.length === 1 ? "" : "s"}
          </span>
        </div>

        {removed ? (
          <div className="enterprise-admin-assigned-role-empty">
            <strong>No active authority</strong>
            <span>
              Membership and enterprise role authority were deactivated when
              this user was removed.
            </span>
          </div>
        ) : (
          <div className="enterprise-admin-authority-source-list">
            <div className="enterprise-admin-authority-source">
              <div className="enterprise-admin-authority-source-heading">
                <span>Membership role</span>
                <small>Base authority</small>
              </div>
              <div className="enterprise-admin-assigned-role-card">
                <div>
                  <strong>{humanize(legacyRole || "No membership role")}</strong>
                  <span>
                    {legacyRole
                      ? roleDescription(roles, legacyRole)
                      : "No base membership authority is recorded."}
                  </span>
                </div>
              </div>
            </div>

            <div className="enterprise-admin-authority-source">
              <div className="enterprise-admin-authority-source-heading">
                <span>Enterprise assignments</span>
                <small>{effectiveAssignedRoles.length} active</small>
              </div>

              <div className="enterprise-admin-assigned-role-list">
                {effectiveAssignedRoles.length ? (
                  effectiveAssignedRoles.map((roleKey) => (
                    <div
                      className="enterprise-admin-assigned-role-card"
                      key={roleKey}
                    >
                      <div>
                        <strong>{humanize(roleKey)}</strong>
                        <span>{roleDescription(roles, roleKey)}</span>
                      </div>

                      <button
                        type="button"
                        className="enterprise-admin-danger-text-button"
                        disabled={saving}
                        onClick={() =>
                          onRemoveRole?.(user.user_id, roleKey)
                        }
                      >
                        Remove
                      </button>
                    </div>
                  ))
                ) : (
                  <div className="enterprise-admin-assigned-role-empty compact">
                    <strong>No additional assignments</strong>
                    <span>
                      Runtime authority currently comes from the membership
                      role only.
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </section>

      <section className="enterprise-admin-detail-section">
        <div className="enterprise-admin-card-heading compact">
          <h4>Lifecycle activity</h4>
          <span>{user.recent_activity?.length || 0} recorded event(s)</span>
        </div>

        <div className="enterprise-admin-user-timeline">
          {(user.recent_activity || []).length ? (
            user.recent_activity.map((event) => (
              <article key={event.id}>
                <strong>{humanize(event.new_status)}</strong>
                <span>{event.reason || "No reason recorded"}</span>
                <time>{formatDate(event.created_at)}</time>
              </article>
            ))
          ) : (
            <p>No lifecycle changes have been recorded.</p>
          )}
        </div>
      </section>

      <section className="enterprise-admin-detail-section">
        <div className="enterprise-admin-card-heading compact">
          <h4>Administrative audit</h4>
          <span>{user.audit_timeline?.length || 0} event(s)</span>
        </div>

        <div className="enterprise-admin-user-timeline">
          {(user.audit_timeline || []).length ? (
            user.audit_timeline.map((event) => (
              <article key={event.id}>
                <strong>{timelineLabel(event)}</strong>
                <span>
                  {event.actor_name ||
                    event.actor_id ||
                    "OASYS administrator"}
                </span>
                <time>{formatDate(event.created_at)}</time>
              </article>
            ))
          ) : (
            <p>No user-specific administrative events have been recorded.</p>
          )}
        </div>
      </section>

      {!removed ? (
        <div className="enterprise-admin-user-actions enterprise-admin-detail-actions">
          <button
            type="button"
            disabled={saving}
            onClick={() => onAssignRole?.(user)}
          >
            Assign role
          </button>

          {active ? (
            <button
              type="button"
              className="enterprise-admin-warning-button"
              disabled={saving || isCurrentUser}
              title={
                isCurrentUser
                  ? "You cannot suspend your current session."
                  : undefined
              }
              onClick={() => onLifecycleAction?.("suspend", user)}
            >
              Suspend
            </button>
          ) : null}

          {suspended ? (
            <button
              type="button"
              disabled={saving}
              onClick={() => onLifecycleAction?.("reactivate", user)}
            >
              Reactivate
            </button>
          ) : null}

          <button
            type="button"
            className="enterprise-admin-danger-button"
            disabled={saving || isCurrentUser}
            title={
              isCurrentUser
                ? "You cannot remove your current session."
                : undefined
            }
            onClick={() => onLifecycleAction?.("remove", user)}
          >
            Remove member
          </button>
        </div>
      ) : null}
    </aside>
  );
}
